import React from 'react';
import { LightbulbIcon, CheckCircleIcon, QuoteIcon, BookOpenIcon, CalendarIcon, ClockIcon, MonitorIcon, UserIcon, UsersIcon, StarIcon, HelpCircleIcon } from 'lucide-react';
const CTA_URL = '/webinar-mc-adv1';
const WebinarLink = ({
  children
}: {
  children: React.ReactNode;
}) => <a href={CTA_URL} className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1218">
    {children}
  </a>;
const replaceWebinarWithLink = (text: string) => {
  const parts = text.split(/(\bwebinar\b)/i);
  return parts.map((part, index) => {
    if (part.toLowerCase() === 'webinar') {
      return <WebinarLink key={index} data-id="element-1219">webinar</WebinarLink>;
    }
    return part;
  });
};
const renderStars = (rating: number) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(<StarIcon key={i} className={`w-5 h-5 ${i < rating ? 'text-green-500 fill-green-500' : 'text-gray-200'}`} data-id="element-1220" />);
  }
  return <div className="flex" data-id="element-1221">{stars}</div>;
};
// ============ HEADER ============
const Header = () => <header className="mb-8" data-id="element-1222">
    <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 text-blue-800 leading-tight" data-id="element-1223">
      Onderzoek op 3897 honden en de resultaten zijn schokkend: Straffen maakt
      honden 3 keer agressiever...
    </h1>
    <div className="text-center mb-4 text-blue-600 font-semibold" data-id="element-1224">
      {replaceWebinarWithLink('Amee - de eigenaar van Stressless Dogs - heeft inmiddels honderden baasjes succesvol geholpen om hun hond minder agressief te maken. In dit artikel legt ze uit waarom straffen niet werkt:')}
    </div>
    <div className="w-full mb-6" data-id="element-1225">
      <a href={CTA_URL} target="_blank" rel="noopener noreferrer" data-id="element-1226">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vK5yBdTaFAnzK4LEcjoEMH/freepik__candid-photography-with-natural-textures-and-highl__57096_%281%29.png" alt="Jack Russell terrier die agressief gedrag vertoont aan de lijn" className="w-full h-auto rounded-lg shadow-md cursor-pointer" data-id="element-1227" />
      </a>
      <p className="text-xs text-gray-500 mt-1 text-center italic" data-id="element-1228">
        "Honden die gestraft worden, zijn 3 keer vaker agressief"
      </p>
    </div>
    <div className="w-full h-[1px] bg-gray-200 my-6" data-id="element-1229"></div>
  </header>;
// ============ PROBLEM ============
const Problem = () => <section className="mb-10" data-id="element-1230">
    <p className="mb-6 leading-relaxed" data-id="element-1231">
      Een groot onderzoek in Engeland laat zien dat straffen heel slecht is voor
      honden. Wetenschappers onderzochten bijna 4.000 honden. Ze ontdekten iets
      schokkends.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1232">
      Honden die gestraft worden, zijn 3 keer vaker agressief naar hun baasje.
      Dit betekent dat ze vaker grommen, blaffen of bijten.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1233">
      De onderzoekers waren Casey en zijn team (Casey et al. (2014).) Hun
      onderzoek kwam uit in 2014. Nu waarschuwen de experts ook in Nederland:
      stop met streng straffen.
    </p>
    <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-1234">
      De conclusie van het onderzoek is simpel: Straffen maakt honden
      agressiever.
    </h2>
    <p className="mb-6 leading-relaxed" data-id="element-1235">
      Recent legde Amee het tijdens{' '}
      <a href={CTA_URL} className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1236">
        haar online presentatie
      </a>{' '}
      uit, waar honderden mensen bij aanwezig waren. Ze legde uit dat:
    </p>
    <p className="mb-6 leading-relaxed italic" data-id="element-1237">
      "Honden die gestraft worden gedragen zich slechter...Ze zijn niet
      rustiger. Ze worden juist agressiever en hun probleemgedrag neemt toe. Dit
      is een groot verschil. Straffen werkt dus niet. Het maakt alles erger.
      Bekijk maar de cijfers uit dit onderzoek"
    </p>
    <p className="mb-6 leading-relaxed font-semibold" data-id="element-1238">
      De cijfers uit het onderzoek waren óók schokkend en spreken eigenlijk voor
      zichzelf:
    </p>
    <ul className="mb-6 list-disc pl-5 space-y-2" data-id="element-1239">
      <li data-id="element-1240">
        Honden die alleen beloond en positief getrained worden: 12 van de 100
        zijn agressief
      </li>
      <li data-id="element-1241">Honden die gestraft worden: 36 van de 100 zijn agressief</li>
    </ul>
  </section>;
// ============ SOLUTION ============
const Solution = () => <section className="mb-10" data-id="element-1242">
    <div className="bg-blue-50 p-5 rounded-lg mb-8 border-l-4 border-blue-500" data-id="element-1243">
      <h2 className="text-xl font-bold text-blue-800 flex items-center mb-4" data-id="element-1244">
        <LightbulbIcon className="w-6 h-6 mr-2 text-blue-600" data-id="element-1245" />
        Hoe ga je dan om met probleemgedrag? Want je houdt van je hond en wil
        dat hij/zij blij is en zich goed gedraagt...
      </h2>
      <p className="mb-4 leading-relaxed" data-id="element-1246">
        Volgens Amee werken hondenscholen nog vaak met achterhaalde ideeën over
        dominantie en conditionering, zegt ze tijdens haar webinar. Maar
        tegenwoordig weten we dat:
      </p>
      <ul className="mb-4 list-disc pl-5 space-y-2" data-id="element-1247">
        <li data-id="element-1248">Probleemgedrag een uiting is van een onderliggend probleem</li>
        <li data-id="element-1249">
          De meeste honden stress ervaren zonder dat het baasje dit doorheeft
        </li>
        <li data-id="element-1250">
          Als baasjes communicatie signalen van hun hond leren herkennen, ze
          problemen kunnen voorkomen
        </li>
        <li data-id="element-1251">
          Wanneer een baasje leert om zijn lichaamstaal te matchen met zijn
          commando's, dat honden hen dan veel beter begrijpen en ineens wel
          luisteren.
        </li>
      </ul>
      <p className="text-lg font-semibold text-blue-800 mb-4" data-id="element-1252">
        Door te leren communiceren kunnen baasjes leren om te gaan met hun hond
        zonder te straffen of corrigeren. Problemen worden hierdoor minder en de
        hond veel gelukkiger.
      </p>
      <p className="leading-relaxed mb-4" data-id="element-1253">
        In het verleden heeft Amee deze presentatie al voor duizenden
        hondenbaasjes gegeven. Deze week geeft ze hem nogmaals voor elk baasje
        die de agressiviteit van zijn of haar hond liefdevol wil oplossen.
      </p>
      <a href={CTA_URL} className="block w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 text-center" target="_blank" rel="noopener noreferrer" data-id="element-1254">
        BEKIJK BESCHIKBAARHEID WEBINAR
      </a>
    </div>
    <div className="w-full mb-6" data-id="element-1255">
      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iETwyZQdRtoqRfKLXsTijB/Scherm_afbeelding_2025-09-01_om_23.36.13.png" alt="Border Collie met gespannen houding in woonkamer" className="w-full h-auto rounded-lg shadow-md" data-id="element-1256" />
      <p className="text-xs text-gray-500 mt-1 text-center italic" data-id="element-1257">
        "Straffen maakt honden gestrest. Een gestressde hond is sneller
        agressief."
      </p>
    </div>
    <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-1258">
      Gedragsdeskundige Amee: Straffen zorgt voor stress, stress zorgt voor
      problemen
    </h2>
    <p className="mb-6 leading-relaxed" data-id="element-1259">
      Amee, hondengedragsexpert bij Stressless Dogs, legt in{' '}
      <a href={CTA_URL} className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1260">
        haar online presentatie
      </a>{' '}
      uit dat stress de oorzaak is van de meeste problemen bij honden. Een hond
      met stress wordt prikkelbaarder en daarom soms ook agressief.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1261">Wat gebeurt er:</p>
    <div className="mb-6 pl-5 space-y-2" data-id="element-1262">
      <p data-id="element-1263">
        <span className="font-medium" data-id="element-1264">1.</span> Je straft je hond
      </p>
      <p data-id="element-1265">
        <span className="font-medium" data-id="element-1266">2.</span> Je hond wordt gestresst
      </p>
      <p data-id="element-1267">
        <span className="font-medium" data-id="element-1268">3.</span> Je hond is hierdoor
        prikkelbaarder
      </p>
      <p data-id="element-1269">
        <span className="font-medium" data-id="element-1270">4.</span> Je hond reageert sneller
        agressief op zijn omgeving
      </p>
      <p data-id="element-1271">
        <span className="font-medium" data-id="element-1272">5.</span> Jij straft je hond nog meer
      </p>
    </div>
    <p className="mb-6 leading-relaxed" data-id="element-1273">
      Bovendien begrijpt een hond niet waarom hij gestraft wordt en weet hij
      niet wat hij dan wel moet doen. Baasjes weten slecht hoe ze ervoor kunnen
      zorgen dat hun hond wel gewenst gedrag laat zien. Daarom geef ik vandaag
      en morgen hier weer gratis online presentaties over (
      <a href={CTA_URL} className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1274">
        hier meer info
      </a>
      )
    </p>
  </section>;
// ============ TESTIMONIALS ============
const Testimonials = () => <section className="mb-10" data-id="element-1275">
    <h2 className="text-xl font-bold text-blue-800 mb-6" data-id="element-1276">
      Nederlandse expert leert baasjes de oplossing tijdens online presentatie
    </h2>
    <div className="space-y-6 mb-6" data-id="element-1277">
      <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-300" data-id="element-1278">
        <div className="flex flex-col md:flex-row mb-3" data-id="element-1279">
          <div className="mb-4 md:mb-0 md:mr-4 flex-shrink-0 flex justify-center" data-id="element-1280">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kJ5b6tBbH8hDNt52JG84mT/Scherm%C2%ADafbeelding_2025-08-15_om_15.44.02.png" alt="Amee de Bruijn" className="w-16 h-16 rounded-full object-cover" data-id="element-1281" />
          </div>
          <div data-id="element-1282">
            <QuoteIcon className="w-6 h-6 text-blue-300 mr-2 flex-shrink-0 float-left" data-id="element-1283" />
            <p className="italic" data-id="element-1284">
              "Ik zie elke dag honden die agressief zijn geworden door verkeerde
              training. Mensen willen het beste voor hun hond. Maar ze krijgen
              slecht advies."
            </p>
            <p className="text-right text-sm font-medium mt-2" data-id="element-1285">
              - Amee de Bruijn, Hondengedragsdeskundige
            </p>
          </div>
        </div>
      </div>
    </div>
    <p className="mb-6 leading-relaxed" data-id="element-1286">
      Amee de Bruijn werkt al jaren met honden. Ook geeft ze online presentaties
      waarin ze hondenbaasjes helpt om hun hond beter te begrijpen.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1287">
      Amee heeft al meer dan 15.000 mensen geholpen. Ze gebruikt nooit straffen.
      In plaats daarvan leert ze baasjes "hondentaal". Zo kunnen ze zonder
      straffen of corrigeren hun hond laten weten dat die iets niet hoeft te
      doen. "Als je dit op een manier doet die voor de hond natuurlijk is dan
      snapt hij dat meteen" zegt Amee. "Ook leren mensen hoe ze hun hond moeten
      lezen. Dan begrijpen ze wat hij denkt of voelt. Dit maakt alles
      makkelijker."
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1288">
      "Honden willen samenwerken," legt ze uit tijdens haar online presentatie.
      "Je hoeft ze niet te straffen. Je moet alleen hun taal leren spreken."
    </p>
    <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-1289">
      Samenwerken in plaats van straffen
    </h2>
    <p className="mb-6 leading-relaxed" data-id="element-1290">
      Wetenschap laat zien dat je beter kan samenwerken. Dit begint bij je hond
      begrijpen en leren hoe je met hem communiceert.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1291">
      "Daarom geef ik af en toe gratis online webinars over hondentaal. Hierin
      leer ik mensen hoe ze met liefdevolle communicatie probleemgedrag kunnen
      voorkomen, zonder straffen." zegt Amee. "En de band met je hond wordt ook
      nog eens sterker."
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1292">
      <a href={CTA_URL} className="text-blue-600 cursor-pointer underline" target="_blank" rel="noopener noreferrer" data-id="element-1293">
        Ook deze week geef ik deze online presentatie nog een paar keer per dag.
        (Klik hier om te zien of er nog plek is)
      </a>
    </p>
    <p className="mb-6 leading-relaxed font-semibold text-blue-800" data-id="element-1294">
      De waarheid: Honden hebben meer baat bij een begripvol baasje die hun taal
      spreekt, luistert en samenwerkt dan bij een baas die streng is.
    </p>
  </section>;
// ============ WEBINAR DETAILS ============
const WebinarDetails = () => <section className="mb-10" data-id="element-1295">
    <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-1296">
      Het webinar "Communiceren met je hond"
    </h3>
    <p className="mb-6 leading-relaxed" data-id="element-1297">
      Dit is een online presentatie van 90 minuten. Gegeven door Amee. Je kunt
      het volgen vanaf je telefoon, tablet of computer. Het gaat over hoe je
      zonder straffen toch met je hond goed kunt samenwerken. Probleemgedrag
      leer je voorkomen in plaats van afstraffen. Om dit te volgen hoef je
      nergens naartoe te reizen - je volgt het gewoon thuis, op kantoor of
      onderweg via een internetverbinding. Tijdens het webinar kun je via de
      chat vragen stellen aan Amee, en na afloop ontvang je automatisch de
      opname zodat je alles terug kunt kijken wanneer het jou uitkomt (
      <a href={CTA_URL} className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1298">
        klik hier om ook mee te doen
      </a>
      ).
    </p>
    <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-1299">
      Waarom 121.419 hondenbaasjes dit webinar al keken
    </h3>
    <p className="mb-6 leading-relaxed" data-id="element-1300">
      Traditionele trainingsmethoden focussen op het veranderen van
      hondengedrag. Maar Amee's benadering is fundamenteel anders: in plaats van
      je hond te veranderen, leer je zijn taal begrijpen.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1301">
      "Het is als het leren van gebarentaal," legt ze uit. "Je leert hoe honden
      onderling elkaar subtiele signalen geven met hun lichaam. Aan deze
      signalen kan je zien wat ze voelen en willen. Ook leer je hoe je als mens
      deze signalen kan gebruiken om jouw hond iets te vertellen in zijn taal."
    </p>
    <div className="bg-blue-50 p-5 rounded-lg mb-6 border-l-4 border-blue-500" data-id="element-1302">
      <ul className="space-y-3" data-id="element-1303">
        <li className="flex items-start" data-id="element-1304">
          <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1305" />
          <span data-id="element-1306">
            De vijf basis-signalen die elke hond gebruikt en wat ze betekenen
          </span>
        </li>
        <li className="flex items-start" data-id="element-1307">
          <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1308" />
          <span data-id="element-1309">
            Hoe je stress herkent voordat het escaleert naar probleemgedrag
          </span>
        </li>
        <li className="flex items-start" data-id="element-1310">
          <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1311" />
          <span data-id="element-1312">
            De drie grootste misverstanden over honden die 92% van de baasjes
            nog hebben
          </span>
        </li>
        <li className="flex items-start" data-id="element-1313">
          <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1314" />
          <span data-id="element-1315">
            Direct toepasbare technieken om terug te communiceren in zijn taal
          </span>
        </li>
      </ul>
    </div>
    <p className="mb-6 leading-relaxed" data-id="element-1316">
      Maar het echte geheim zit in de volgorde waarin Amee deze kennis deelt.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1317">
      "Ik begin met de signalen die mensen het meest herkennen, maar verkeerd
      interpreteren," legt ze uit. "Zodra ze doorhebben dat veel van het
      dominante gedrag eigenlijk heel iets anders betekent, valt alles op zijn
      plaats. Dan zijn de overige signalen logische uitbreidingen."
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1318">
      "Veel mensen vragen zich af of ze online wel iets over hun hond kunnen
      leren," grapt Amee. "Maar omdat hondentaal voor alle honden hetzelfde is
      kan ik je met voorbeeld video's en een goede presentatie al super veel
      leren over je eigen hond."
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1319">
      "Honden zijn geen mysterieuze wezens," zegt Amee. "Hondentaal is subtiel,
      maar glashelder. Je moet alleen leren waar je naar moet kijken. Daar help
      ik mensen mee via{' '}
      <a href={CTA_URL} className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1320">
        dit webinar
      </a>
      "
    </p>
    <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-1321">
      Revolutie onder hondenbaasjes
    </h3>
    <div className="space-y-6 mb-6" data-id="element-1322">
      <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-300" data-id="element-1323">
        <p className="italic mb-2" data-id="element-1324">
          "Na 20 minuten begreep ik al waarom onze wandelingen altijd chaotisch
          waren. Luna probeerde me de hele tijd te vertellen dat ze zich
          onveilig voelde, maar ik interpreteerde het als koppigheid. Het moment
          dat ik dat doorhad, veranderde alles."
        </p>
        <p className="text-right text-sm font-medium" data-id="element-1325">- Lenneke uit Utrecht</p>
      </div>
      <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-300" data-id="element-1326">
        <p className="italic mb-2" data-id="element-1327">
          "Mijn vrouw en ik keken elkaar halverwege het webinar aan. 'Daarom
          doet hij dat!' zeiden we tegelijk. Plotseling maakten alle rare
          gewoontes van Rex perfect zin."
        </p>
        <p className="text-right text-sm font-medium" data-id="element-1328">- Thomas uit Antwerpen</p>
      </div>
      <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-300" data-id="element-1329">
        <p className="italic mb-2" data-id="element-1330">
          "Het was emotioneel. Ik realiseerde me dat Buddy mij al maanden
          probeerde iets te vertellen over zijn angst, en ik dacht maar dat hij
          ongehoorzaam was. Ik voelde me schuldig, maar ook opgelucht. Eindelijk
          begreep ik hem."
        </p>
        <p className="text-right text-sm font-medium" data-id="element-1331">- Sandra uit Amsterdam</p>
      </div>
    </div>
    <div className="bg-blue-50 p-6 rounded-lg mb-8 border border-blue-200" data-id="element-1332">
      <div className="flex items-center mb-4" data-id="element-1333">
        <CalendarIcon className="w-5 h-5 text-blue-600 mr-2" data-id="element-1334" />
        <h3 className="font-bold" data-id="element-1335">Webinar informatie</h3>
      </div>
      <p className="mb-4 leading-relaxed font-bold" data-id="element-1336">
        Gratis webinar "Communiceren met je hond"
      </p>
      <ul className="mb-4 space-y-2" data-id="element-1337">
        <li data-id="element-1338">
          <strong data-id="element-1339">Wanneer:</strong> Vandaag en morgen
        </li>
        <li data-id="element-1340">
          <strong data-id="element-1341">Tijden:</strong> 10:00, 13:00 en 20:00
        </li>
        <li data-id="element-1342">
          <strong data-id="element-1343">Waar:</strong> Online (thuis volgen)
        </li>
        <li data-id="element-1344">
          <strong data-id="element-1345">Kosten:</strong> Helemaal gratis
        </li>
      </ul>
      <p className="mb-4 leading-relaxed" data-id="element-1346">
        Je kunt het webinar volgen op je telefoon, tablet of computer. Je hoeft
        nergens naartoe.
      </p>
      <div className="bg-white p-4 rounded-lg border border-blue-200 mb-5" data-id="element-1347">
        <p className="font-bold text-blue-800 mb-2" data-id="element-1348">Tijd dringt</p>
        <p className="text-blue-800" data-id="element-1349">
          Het onderzoek is duidelijk: straffen is gevaarlijk. Elke dag dat je
          ermee doorgaat, wordt het erger.
        </p>
      </div>
      <a href={CTA_URL} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 block text-center" target="_blank" rel="noopener noreferrer" data-id="element-1350">
        JA, IK WIL DE WAARHEID WETEN - GRATIS AANMELDEN
      </a>
    </div>
    <h2 className="text-xl font-bold text-blue-800 mb-4 flex items-center" data-id="element-1351">
      <MonitorIcon className="w-6 h-6 mr-2 text-blue-600" data-id="element-1352" />
      Wat Amee tijdens het webinar vertelt
    </h2>
    <div className="space-y-4 mb-8" data-id="element-1353">
      {[{
      n: 1,
      title: 'Minuut 1-20: Waarom straffen gevaarlijk is',
      text: 'Amee legt uit wat het onderzoek van 4.000 honden heeft laten zien. Waarom straffen honden agressief maakt. En waarom veel mensen dit niet weten.'
    }, {
      n: 2,
      title: 'Minuut 21-35: Hoe honden echt communiceren',
      text: 'Je leert hoe honden met elkaar praten. Welke signalen ze gebruiken. En waarom dit anders is dan wat veel mensen denken.'
    }, {
      n: 3,
      title: 'Minuut 36-55: De signalen van je hond begrijpen',
      text: 'Je leert de belangrijkste tekens die honden geven. Wat ze betekenen. En hoe je kunt zien wat je hond echt wil zeggen.'
    }, {
      n: 4,
      title: 'Minuut 56-75: Terug praten in hondentaal',
      text: 'Hoe je je hond kunt vertellen wat je wilt. Op een manier die hij begrijpt. Zonder straffen of schreeuwen.'
    }, {
      n: 5,
      title: 'Minuut 76-90: Jouw vragen beantwoorden',
      text: 'Amee beantwoordt vragen van mensen die meedoen. Ook vertelt ze over een speciale verrassing.'
    }].map(item => <div className="flex" key={item.n} data-id="element-1354">
          <div className="bg-blue-600 text-white rounded-full min-w-8 h-8 flex items-center justify-center font-bold mr-3" data-id="element-1355">
            {item.n}
          </div>
          <div data-id="element-1356">
            <h4 className="font-bold" data-id="element-1357">{item.title}</h4>
            <p className="text-sm text-gray-700" data-id="element-1358">{item.text}</p>
          </div>
        </div>)}
    </div>
    <p className="mb-6 leading-relaxed" data-id="element-1359">
      "Rond minuut 20 begrijpen mensen het," vertelt Amee. "Dan zie ik in de
      chat dat het kwartje valt. Ze snappen waarom hun oude manier van doen niet
      werkte."
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1360">
      <a href={CTA_URL} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1361">
        Klik hier om jezelf aan te melden voor het webinar.
      </a>
    </p>
    <div className="bg-blue-50 p-6 rounded-lg mb-8 border border-blue-200" data-id="element-1362">
      <div className="flex items-center mb-4" data-id="element-1363">
        <CalendarIcon className="w-5 h-5 text-blue-600 mr-2" data-id="element-1364" />
        <h3 className="font-bold" data-id="element-1365">Dit webinar kan snel volgeboekt zijn</h3>
      </div>
      <p className="mb-4 leading-relaxed" data-id="element-1366">
        Ik moet eerlijk tegen je zijn - de vraag naar deze informatie is gewoon
        enorm.
      </p>
      <p className="mb-4 leading-relaxed" data-id="element-1367">
        Sinds de resultaten van dit onderzoek meer bekend worden, melden zich
        elke week meer baasjes aan die eindelijk de waarheid willen weten.
      </p>
      <p className="mb-4 leading-relaxed" data-id="element-1368">
        Het probleem is dat elk webinar maximaal 75 plekken heeft. En als alle
        plekken vol zijn? Dan kan ik pas volgende maand nieuwe sessies
        organiseren.
      </p>
      <div className="bg-white p-4 rounded-lg border border-blue-200 mb-5" data-id="element-1369">
        <p className="font-bold text-blue-800 mb-2" data-id="element-1370">
          Dit kan je enige kans zijn:
        </p>
        <p className="text-blue-800" data-id="element-1371">
          Het organiseren van deze gratis webinars kost me enorm veel tijd en
          energie. Ik kan niet garanderen wanneer ik weer een reeks volgende
          gratis sessies doe.
        </p>
      </div>
      <a href={CTA_URL} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 block text-center" target="_blank" rel="noopener noreferrer" data-id="element-1372">
        JA, IK WIL DE WAARHEID WETEN - GRATIS AANMELDEN
      </a>
    </div>
    <div className="bg-green-50 p-6 rounded-lg border border-green-200 mb-8" data-id="element-1373">
      <h3 className="text-lg font-bold text-green-800 mb-4" data-id="element-1374">
        Over het populaire webinar van Amee: "Communiceren met je hond"
      </h3>
      <ul className="space-y-3 mb-5" data-id="element-1375">
        <li className="flex items-start" data-id="element-1376">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1377" />
          <span data-id="element-1378">Volledig gratis - geen verborgen kosten</span>
        </li>
        <li className="flex items-start" data-id="element-1379">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1380" />
          <span data-id="element-1381">Te volgen vanaf telefoon, tablet of computer</span>
        </li>
        <li className="flex items-start" data-id="element-1382">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1383" />
          <span data-id="element-1384">Opname wordt toegestuurd</span>
        </li>
        <li className="flex items-start" data-id="element-1385">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1386" />
          <span data-id="element-1387">Maximaal 75 plekken per sessie</span>
        </li>
        <li className="flex items-start" data-id="element-1388">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1389" />
          <span data-id="element-1390">
            Speciale verrassingsbonus tijdens het{' '}
            {replaceWebinarWithLink('webinar')}
          </span>
        </li>
      </ul>
      <p className="font-bold mb-5" data-id="element-1391">Hoe krijg je toegang:</p>
      <ol className="list-decimal pl-8 mb-5 space-y-2" data-id="element-1392">
        <li data-id="element-1393">Klik de aanmeldbutton hieronder</li>
        <li data-id="element-1394">Kies je tijdslot (vandaag of morgen)</li>
        <li data-id="element-1395">Vul het korte aanmeldformulier in</li>
        <li data-id="element-1396">Ontvang direct je toegangslink via email</li>
        <li data-id="element-1397">
          Klik 5 minuten voor de start op de link in mail (het webinar start dan
          automatisch. Je hebt geen software nodig.)
        </li>
      </ol>
      <a href={CTA_URL} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 block text-center" target="_blank" rel="noopener noreferrer" data-id="element-1398">
        JA, IK WIL DE WAARHEID WETEN - GRATIS AANMELDEN
      </a>
    </div>
  </section>;
// ============ REVIEWS ============
type Review = {
  name: string;
  reviews: string;
  country: string;
  date: string;
  rating: number;
  title: string;
  body: React.ReactNode;
  bodyDate?: string;
  avatarImg?: string;
  initials?: string;
  initialsBg?: string;
  verified?: boolean;
};
const reviews: Review[] = [{
  name: 'R.Bosma',
  reviews: '3 reviews',
  country: 'NL',
  date: '21 mei 2025',
  rating: 5,
  avatarImg: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2oWBifrk1zfqZg5zYRSTen/Ontwerp_zonder_titel_%2845%29.png',
  verified: true,
  title: 'Hele fijne webinar',
  body: <>
        <p className="leading-relaxed mb-3" data-id="element-1399">
          Ik had al veel hondentrainingen gevolgd en certificaten gehaald, maar
          toch kreeg mijn hond gedragsproblemen, zoals uitvallen en niet goed
          luisteren. Dat frustreerde me, want ik wist niet goed wat ik verkeerd
          deed. Ik ben begonnen met 'Liefdevol communiceren' en dit heeft echt
          een verschil gemaakt.
        </p>
        <p className="leading-relaxed" data-id="element-1400">
          Ik begrijp nu veel beter wat mijn hond wél en niet snapt, en daardoor
          kan ik veel duidelijker en consequenter zijn. Mijn zelfvertrouwen is
          gegroeid en ik voel me rustiger in het contact met mijn hond. Hierdoor
          vertrouwt mijn hond ook meer op mij. De samenwerking is veel prettiger
          geworden – ik weet wat ik doe en waarom het werkt. Dat geeft rust,
          voor mij én voor mijn inmiddels honden.
        </p>
      </>,
  bodyDate: '30 april 2025'
}, {
  name: 'Winok',
  reviews: '1 review',
  country: 'BE',
  date: '3 okt 2024',
  rating: 5,
  avatarImg: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4qKke9UKsqE4XLCzHK99xw/73x73_%284%29.png',
  verified: true,
  title: 'Fijne manier om met honden om te gaan.',
  body: <p className="leading-relaxed" data-id="element-1401">
        Fijne manier om met honden om te gaan.. ik heb een hondenuitlaatservice
        en wandel stressless met vijf honden... geeft rust voor zowel de honden
        als mezelf. Drie meter lijn werkt prima met meerdere honden of zoals
        door Amee geleerd
      </p>,
  bodyDate: '28 september 2024'
}, {
  name: 'Margreet',
  reviews: '4 reviews',
  country: 'NL',
  date: '20 apr 2024',
  rating: 5,
  initials: 'MA',
  initialsBg: 'bg-red-100',
  title: 'Dit was de eerste keer dat ik een...',
  body: <p className="leading-relaxed" data-id="element-1402">
        Dit was de eerste keer dat ik een online hondencursus volgde en ik heb
        er veel van opgestoken. Hoewel mijn honden zich (meestal) prima
        gedragen, wilde ik vooral leren wat ik zelf nog een beter kan doen ivm
        de communicatie. Dat is gelukt, het valt echt op dat omdat ik mij zelf
        anders opstel, mijn honden nu veel rustiger communiceren met andere
        honden. Vooral met onbekende honden was er toch vaak wel flink geblaf.
        Nu bijna niet meer, heerlijk, wat is wandelen toch/weer een genot. Idd:
        stressless!
      </p>
}, {
  name: 'René de Jong',
  reviews: '6 reviews',
  country: 'NL',
  date: '14 mrt 2024',
  rating: 5,
  avatarImg: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eXGLUSGB8WpmCCqjk27tjh/73x73_%283%29.png',
  verified: true,
  title: 'Het werkt echt',
  body: <p className="leading-relaxed" data-id="element-1403">
        We hebben het gevoel dat wij zelf de hondentaal beter begrijpen of zoals
        in de cursus geleerd: de honden beter kunnen "lezen". Aanrader het werkt
        echt.
      </p>,
  bodyDate: '22 februari 2024'
}, {
  name: 'Gerard Dekkers',
  reviews: '2 reviews',
  country: 'NL',
  date: '20 apr 2025',
  rating: 4,
  avatarImg: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fQqJmPyJGyoub8Zq1U3aDc/73x73_%282%29.png',
  title: 'Goede online webinar met heldere uitleg',
  body: null,
  bodyDate: '22 maart 2025'
}, {
  name: 'Blije hondenbaas',
  reviews: '1 review',
  country: 'NL',
  date: '9 aug 2025',
  rating: 4,
  initials: 'BH',
  initialsBg: 'bg-orange-100',
  title: 'We merkten meteen verschil',
  body: <p className="leading-relaxed" data-id="element-1404">
        We merkten meteen verschil bij onze hond, na toepassen van een aantal
        vaardigheden. Wij begrijpen haar beter en zij begrijpt ons beter. Er is
        veel meer rust in haar gedrag en we hebben nog meer plezier in de omgang
        met haar. Bedankt, stressless dogs en Amee!
      </p>,
  bodyDate: '25 juli 2025'
}, {
  name: 'Tine',
  reviews: '4 reviews',
  country: 'BE',
  date: '3 sep 2024',
  rating: 5,
  initials: 'TI',
  initialsBg: 'bg-yellow-100',
  title: 'Niet goed maar uitstekend',
  body: <>
        <p className="leading-relaxed mb-3" data-id="element-1405">Niet goed maar uitstekend!</p>
        <p className="leading-relaxed" data-id="element-1406">
          Amée is een zeer lieve vrouw met een hart voor honden en dat merk je
          zeer goed. De tips helpen echt. Ik raad iedereen met puppy's, jongere
          hondjes en alle andere leeftijden deze cursus aan, vooral mensen die
          problemen hebben met hun troetel. Zowel gedragsproblemen als medische
          problemen. Volg even de gratis webinar en je bent volledig overtuigd
          van de cursus en de mensen erachter. Heel veel liefs aan iedereen van
          het team. Groetjes Tine Oetzmann
        </p>
      </>
}, {
  name: 'Nicolette',
  reviews: '2 reviews',
  country: 'NL',
  date: '8 jul 2024',
  rating: 5,
  initials: 'NI',
  initialsBg: 'bg-yellow-100',
  verified: true,
  title: 'Hele fijne cursus. Aanrader zelfs voor de ervaren honden liefhebber.',
  body: <>
        <p className="leading-relaxed mb-3" data-id="element-1407">
          Nu hebben wij altijd herplaatsers, dus we zijn wel wat gewend. Maar
          bij onze laatste herplaatser hebben we wel hulp nodig.
        </p>
        <p className="leading-relaxed" data-id="element-1408">
          Het is netjes overzichtelijk, duidelijk maar wat met het meeste
          aanspreekt is dat het zonder commando's gaat maar via communiceren.
        </p>
      </>
}, {
  name: 'Smit',
  reviews: '11 reviews',
  country: 'NL',
  date: '31 jul 2025',
  rating: 5,
  initials: 'SM',
  initialsBg: 'bg-purple-100',
  title: 'Laagdrempelige training',
  body: <p className="leading-relaxed" data-id="element-1409">
        Laagdrempelige training. De 1e tips heb ik direct toegepast. Het webinar
        was een eye-opener en het adaarop volgende aanbod is zeker de moeite
        waard.
      </p>,
  bodyDate: '31 juli 2025'
}];
const Reviews = () => <section className="mb-10" data-id="element-1410">
    <h2 className="text-xl font-bold text-blue-800 mb-6" data-id="element-1411">
      Wat deelnemers zeggen over het {replaceWebinarWithLink('webinar')}
    </h2>
    <div className="space-y-8" data-id="element-1412">
      {reviews.map((r, idx) => <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1413">
          <div className="bg-gray-50 p-4 flex items-center justify-between" data-id="element-1414">
            <div className="flex items-center" data-id="element-1415">
              {r.avatarImg ? <img src={r.avatarImg} alt={`${r.name} profile`} className="w-10 h-10 rounded-full object-cover mr-3" data-id="element-1416" /> : <div className={`w-10 h-10 rounded-full ${r.initialsBg} flex items-center justify-center text-gray-700 font-bold mr-3`} data-id="element-1417">
                  {r.initials}
                </div>}
              <div data-id="element-1418">
                <div className="flex items-center" data-id="element-1419">
                  <h3 className="font-bold mr-2" data-id="element-1420">{r.name}</h3>
                  <span className="text-sm text-gray-500" data-id="element-1421">{r.reviews}</span>
                  <span className="mx-2 text-gray-400" data-id="element-1422">•</span>
                  <span className="text-sm text-gray-500" data-id="element-1423">{r.country}</span>
                </div>
                <div className="flex items-center" data-id="element-1424">
                  {renderStars(r.rating)}
                  {r.verified && <div className="ml-2 flex items-center text-gray-500 text-sm" data-id="element-1425">
                      <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-1426" />
                      Geverifieerd
                    </div>}
                </div>
              </div>
            </div>
            <div className="text-gray-500" data-id="element-1427">{r.date}</div>
          </div>
          <div className="p-4" data-id="element-1428">
            <h4 className="font-bold text-lg mb-2" data-id="element-1429">{r.title}</h4>
            {r.body}
            {r.bodyDate && <p className="text-right text-sm text-gray-500 mt-2" data-id="element-1430">
                {r.bodyDate}
              </p>}
          </div>
        </div>)}
    </div>
  </section>;
// ============ FAQ ============
const FAQ = () => <section className="mb-10" data-id="element-1431">
    <h2 className="text-xl font-bold text-blue-800 mb-6" data-id="element-1432">
      Veelgestelde vragen
    </h2>
    <div className="space-y-6" data-id="element-1433">
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1434">
        <div className="bg-gray-50 p-4 flex items-start" data-id="element-1435">
          <HelpCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1436" />
          <h3 className="font-bold" data-id="element-1437">
            "Is er een specifiek moment waarop het kwartje valt?"
          </h3>
        </div>
        <div className="p-4" data-id="element-1438">
          <p className="leading-relaxed" data-id="element-1439">
            "Rond minuut 20 valt het kwartje," vertelt Amee. "Wanneer ik een
            aantal misvattingen over dominantie uit de wereld help, zie ik in de
            chat dat mensen plotseling begrijpen waarom hun aanpak niet werkte."
          </p>
          <p className="leading-relaxed mt-2" data-id="element-1440">
            Voor Marcel was dit het keerpunt: "Toen begreep ik dat Luna en ik
            gewoon langs elkaar heen communiceerden. Zij probeerde samenwerking,
            ik dacht dat ze koppig was."
          </p>
        </div>
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1441">
        <div className="bg-gray-50 p-4 flex items-start" data-id="element-1442">
          <HelpCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1443" />
          <h3 className="font-bold" data-id="element-1444">
            "Waarom is dit {replaceWebinarWithLink('webinar')} gratis?"
          </h3>
        </div>
        <div className="p-4" data-id="element-1445">
          <p className="leading-relaxed" data-id="element-1446">
            Omdat het belangrijk is dat baasjes dit snappen geeft Amee het
            gratis webinar "Communiceren met je hond".
          </p>
          <p className="leading-relaxed mt-2" data-id="element-1447">
            Natuurlijk heeft Amee ook verdere verdiepingscursussen voor wie meer
            wil leren, maar tijdens het webinar krijg je een stevige basis in
            wat David Mech werkelijk bedoelde.
          </p>
        </div>
      </div>
    </div>
  </section>;
// ============ BENEFITS ============
const Benefits = () => <section className="mb-10" data-id="element-1448">
    <h2 className="text-xl font-bold text-blue-800 mb-6" data-id="element-1449">
      De keuze is aan jou
    </h2>
    <p className="mb-6 leading-relaxed" data-id="element-1450">
      Wetenschap laat zien dat we niet moeten straffen. Toch doen veel baasjes
      dit nog wel. Honden zijn hierdoor agressiever en er is meer
      probleemgedrag.
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1451">
      Ga jij wel de stap zetten en dit voortaan wel anders doen?
    </p>
    <p className="mb-6 leading-relaxed" data-id="element-1452">
      Een goede eerste stap is om het webinar van Amee te volgen.{' '}
      <a href={CTA_URL} className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-1453">
        Klik hier om te zien of ze het webinar nog geeft.
      </a>
    </p>
    <div className="bg-blue-50 p-6 rounded-lg border border-blue-100" data-id="element-1454">
      <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-1455">Nu is de vraag:</h3>
      <p className="mb-6 leading-relaxed" data-id="element-1456">
        Ga jij nog langer een methode gebruiken die zelfs de grondlegger heeft
        verworpen? Of kies je voor de wetenschappelijk onderbouwde benadering
        die Mech zou goedkeuren?
      </p>
      <ul className="space-y-4 mb-6" data-id="element-1457">
        <li className="flex items-start" data-id="element-1458">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1459" />
          <span data-id="element-1460">
            <strong data-id="element-1461">Wil je blijven domineren?</strong> Of leren communiceren en
            samenwerken?
          </span>
        </li>
        <li className="flex items-start" data-id="element-1462">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1463" />
          <span data-id="element-1464">
            <strong data-id="element-1465">Wil je blijven straffen?</strong> Of begrijpen waarom je
            hond doet wat hij doet?
          </span>
        </li>
        <li className="flex items-start" data-id="element-1466">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1467" />
          <span data-id="element-1468">
            <strong data-id="element-1469">Wil je blijven forceren?</strong> Of een echte band opbouwen
            op basis van vertrouwen?
          </span>
        </li>
      </ul>
      <p className="italic" data-id="element-1470">
        In 90 minuten leer je eindelijk wat de wetenschapper die de dominantie
        theorie bedacht ECHT bedoelde. Het eerstvolgende gratis{' '}
        {replaceWebinarWithLink('webinar')} is vandaag en morgen.
      </p>
    </div>
  </section>;
// ============ CALL TO ACTION ============
const CallToAction = () => <section className="mb-10" data-id="element-1471">
    <div className="w-full mb-6" data-id="element-1472">
      <a href={CTA_URL} target="_blank" rel="noopener noreferrer" data-id="element-1473">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2FwYxJVjG6C7aeyL5JTuqC/Scherm_afbeelding_2025-08-15_om_15.56.55-min.png" alt="Vrouw communiceert met haar hond in een gezellige woonkamer" className="w-full h-auto rounded-lg shadow-md hover:opacity-95 transition-opacity cursor-pointer" data-id="element-1474" />
      </a>
      <p className="text-xs text-gray-500 mt-1 text-center italic" data-id="element-1475">
        Soms kan anderhalf uur alles veranderen in de relatie met je hond
      </p>
    </div>
    <a href={CTA_URL} className="block bg-red-50 p-3 rounded-lg mb-6 border-2 border-red-300 hover:bg-red-100 transition-colors" target="_blank" rel="noopener noreferrer" data-id="element-1476">
      <p className="text-center font-bold text-red-700" data-id="element-1477">
        ⚠️ BEPERKTE PLEKKEN - Maximaal 75 per sessie ⚠️
      </p>
    </a>
    <div className="bg-gray-50 p-6 rounded-lg mb-8 border border-gray-200 hover:bg-gray-100 transition-colors" data-id="element-1478">
      <div className="flex justify-between items-center mb-4" data-id="element-1479">
        <div data-id="element-1480">
          <h3 className="text-lg font-bold" data-id="element-1481">
            Gratis webinar "Communiceren met je hond"
          </h3>
          <p className="text-gray-600 text-sm" data-id="element-1482">
            Vandaag en morgen • 10:00. 13:00 of 20:00
          </p>
        </div>
        <div className="text-right" data-id="element-1483">
          <span className="text-2xl font-bold text-blue-800" data-id="element-1484">GRATIS</span>
          <p className="text-sm text-green-600" data-id="element-1485">Geen verborgen kosten</p>
        </div>
      </div>
      <a href={CTA_URL} className="block w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-4 rounded-lg transition duration-200 mb-4 animate-pulse text-center" target="_blank" rel="noopener noreferrer" data-id="element-1486">
        JA, IK WIL DE WAARHEID WETEN - GRATIS AANMELDEN
      </a>
      <p className="text-center text-sm text-gray-600 mb-2" data-id="element-1487">
        David L. Mech heeft zijn fout toegegeven. Het is tijd dat de rest van de
        wereld luistert.
      </p>
      <div className="flex justify-center space-x-2" data-id="element-1488">
        <span className="text-yellow-500" data-id="element-1489">★★★★★</span>
        <span className="text-sm text-gray-600" data-id="element-1490">
          Meer dan 100.000 deelnemers in 2025
        </span>
      </div>
    </div>
    <p className="font-semibold text-blue-800 text-center mb-6" data-id="element-1491">
      <a href={CTA_URL} className="hover:underline cursor-pointer" target="_blank" rel="noopener noreferrer" data-id="element-1492">
        Want soms kan 90 minuten alles veranderen.
      </a>
    </p>
  </section>;
// ============ MORE TESTIMONIALS ============
const MoreTestimonials = () => <section className="mb-10" data-id="element-1493">
    <h2 className="text-xl font-bold text-blue-800 mb-6" data-id="element-1494">
      Meer reacties van deelnemers
    </h2>
    <div className="space-y-4" data-id="element-1495">
      <div className="flex items-start" data-id="element-1496">
        <div className="flex-shrink-0 mr-3" data-id="element-1497">
          <div className="w-12 h-12 bg-blue-500 rounded-md flex items-center justify-center text-white text-xl font-bold" data-id="element-1498">
            L
          </div>
        </div>
        <div className="flex-1 bg-gray-50 p-4 rounded-lg" data-id="element-1499">
          <div className="flex justify-between mb-1" data-id="element-1500">
            <span className="font-bold" data-id="element-1501">Liset</span>
            <span className="text-gray-500 text-sm" data-id="element-1502">21:29</span>
          </div>
          <p data-id="element-1503">Super bedankt. Was boeiend en leerzaam.</p>
        </div>
      </div>
      <div className="flex items-start" data-id="element-1504">
        <div className="flex-shrink-0 mr-3" data-id="element-1505">
          <div className="w-12 h-12 bg-purple-500 rounded-md flex items-center justify-center text-white text-xl font-bold" data-id="element-1506">
            L
          </div>
        </div>
        <div className="flex-1 bg-gray-50 p-4 rounded-lg" data-id="element-1507">
          <div className="flex justify-between mb-1" data-id="element-1508">
            <span className="font-bold" data-id="element-1509">Linda</span>
            <span className="text-gray-500 text-sm" data-id="element-1510">21:39</span>
          </div>
          <p data-id="element-1511">
            leuk filmpje :-) Zie mezelf en mijn "hondenkleinkids" :))) aangenaam
            verast.
            <br data-id="element-1512" />
            Dankjewel voor het webinar
          </p>
        </div>
      </div>
      <div className="flex items-start" data-id="element-1513">
        <div className="flex-shrink-0 mr-3" data-id="element-1514">
          <div className="w-12 h-12 bg-purple-500 rounded-md flex items-center justify-center text-white text-xl font-bold" data-id="element-1515">
            K
          </div>
        </div>
        <div className="flex-1 bg-gray-50 p-4 rounded-lg" data-id="element-1516">
          <div className="flex justify-between mb-1" data-id="element-1517">
            <span className="font-bold" data-id="element-1518">Karin</span>
            <span className="text-gray-500 text-sm" data-id="element-1519">21:40</span>
          </div>
          <p data-id="element-1520">Heel erg bedankt, het was heel leerzaam</p>
        </div>
      </div>
      <div className="flex items-start" data-id="element-1521">
        <div className="flex-shrink-0 mr-3" data-id="element-1522">
          <div className="w-12 h-12 bg-blue-500 rounded-md flex items-center justify-center text-white text-xl font-bold" data-id="element-1523">
            A
          </div>
        </div>
        <div className="flex-1 bg-gray-50 p-4 rounded-lg" data-id="element-1524">
          <div className="flex justify-between mb-1" data-id="element-1525">
            <span className="font-bold" data-id="element-1526">Annemieke</span>
            <span className="text-gray-500 text-sm" data-id="element-1527">21:40</span>
          </div>
          <p data-id="element-1528">heel erg bedankt voor het webinar! groetjes, Annemieke</p>
        </div>
      </div>
      <div className="flex items-start" data-id="element-1529">
        <div className="flex-shrink-0 mr-3" data-id="element-1530">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/e2XikCMQaZbQPPWTtN2TFx/Jessica_mail_MCC_2.png" alt="Jessica" className="w-12 h-12 object-cover rounded-md" data-id="element-1531" />
        </div>
        <div className="flex-1 bg-gray-50 p-4 rounded-lg" data-id="element-1532">
          <div className="flex justify-between mb-1" data-id="element-1533">
            <span className="font-bold" data-id="element-1534">Jessica</span>
            <span className="text-gray-500 text-sm" data-id="element-1535">aan mij</span>
          </div>
          <p className="mb-2" data-id="element-1536">Hi Amee,</p>
          <p className="mb-2" data-id="element-1537">Super, het is gelukt. Dankjewel!</p>
          <p className="mb-2" data-id="element-1538">
            Ik wilde nog mijn complimenten geven voor het webinar van gisteren.
            Ik heb menig gratis {replaceWebinarWithLink('webinar')}s gevolgd en
            vond die van jou echt wel de beste. Ben benieuwd naar de cursussen!
          </p>
          <p data-id="element-1539">Groeten, Jessica</p>
        </div>
      </div>
      <div className="flex items-start" data-id="element-1540">
        <div className="flex-shrink-0 mr-3" data-id="element-1541">
          <div className="w-12 h-12 bg-purple-500 rounded-md flex items-center justify-center text-white text-xl font-bold" data-id="element-1542">
            C
          </div>
        </div>
        <div className="flex-1 bg-gray-50 p-4 rounded-lg" data-id="element-1543">
          <div className="flex justify-between mb-1" data-id="element-1544">
            <span className="font-bold" data-id="element-1545">Corrie</span>
            <span className="text-gray-500 text-sm" data-id="element-1546">21:40</span>
          </div>
          <p data-id="element-1547">Bedankt voor je heldere uitleg! Fijne avond</p>
        </div>
      </div>
    </div>
  </section>;
// ============ FOOTER ============
const Footer = () => <footer className="border-t border-gray-200 pt-6 text-sm text-gray-600" data-id="element-1548">
    <div className="flex items-start mb-6" data-id="element-1549">
      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kJ5b6tBbH8hDNt52JG84mT/Scherm%C2%ADafbeelding_2025-08-15_om_15.44.02.png" alt="Amee de Bruijn" className="w-16 h-16 rounded-full object-cover mr-4 flex-shrink-0" data-id="element-1550" />
      <div data-id="element-1551">
        <p className="font-semibold mb-2" data-id="element-1552">
          Amee de Bruijn is gediplomeerd hondengedragsconsultant niveau 5,
          opgeleid bij Calming Signs in Koningslust (2017).
        </p>
        <p data-id="element-1553">
          "Natuurlijk heb ik ook verdere verdiepingscursussen als mensen nog
          meer willen leren, maar tijdens het{' '}
          {replaceWebinarWithLink('webinar')} probeer ik baasjes een stevige
          basis te geven in wat David Mech werkelijk bedoelde."
        </p>
      </div>
    </div>
    <div className="text-xs border-t border-gray-200 pt-4 pb-2" data-id="element-1554">
      <p className="text-gray-500 mb-3" data-id="element-1555">
        Meta Advertising Disclaimer: Deze website maakt gebruik van Meta Pixel
        voor advertentiedoeleinden. Dit betekent dat informatie over uw bezoek
        aan deze website wordt gedeeld met Meta (Facebook) om gerichte
        advertenties te kunnen tonen. Door gebruik te maken van deze website
        gaat u akkoord met het gebruik van cookies en vergelijkbare
        technologieën voor analytische en advertentiedoeleinden. Deze pagina is
        een advertorial voor Stressless Dogs. Stressless Dogs is een
        onafhankelijk bedrijf en is op geen enkele wijze verbonden aan of
        onderdeel van Meta (voorheen Facebook). Deze advertentie wordt getoond
        via het Meta advertentieplatform, maar Meta is niet verantwoordelijk
        voor de inhoud of diensten aangeboden door Stressless Dogs.
      </p>
      <p className="text-center text-gray-500" data-id="element-1556">
        © 2025 Stressless Dogs. Alle rechten voorbehouden.
      </p>
    </div>
  </footer>;
// ============ STICKY MOBILE BUTTON ============
const StickyMobileButton = () => {
  const getTodayDate = () => {
    const today = new Date();
    const day = today.getDate();
    const monthNames = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    const month = monthNames[today.getMonth()];
    return `${day} ${month}`;
  };
  return <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white shadow-lg border-t-2 border-gray-200 flex flex-col items-center" data-id="element-1557">
      <p className="text-sm text-gray-600 mb-2" data-id="element-1558">
        Vandaag {getTodayDate()} • 10:00, 13:00 of 20:00
      </p>
      <a href={CTA_URL} target="_blank" rel="noopener noreferrer" className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg transition duration-200 shadow-md text-center" data-id="element-1559">
        Gratis Webinar: Communiceren Met Je Hond (zonder straffen)
      </a>
    </div>;
};
// ============ PAGE ============
export function BlogStraffenAgressiePage() {
  return <div className="w-full bg-white min-h-screen" data-id="element-1560">
      <div className="max-w-3xl mx-auto px-4 py-6 font-sans text-gray-800 pb-32" data-id="element-1561">
        <Header data-id="element-1562" />
        <Problem data-id="element-1563" />
        <Solution data-id="element-1564" />
        <Testimonials data-id="element-1565" />
        <WebinarDetails data-id="element-1566" />
        <Reviews data-id="element-1567" />
        <FAQ data-id="element-1568" />
        <Benefits data-id="element-1569" />
        <CallToAction data-id="element-1570" />
        <MoreTestimonials data-id="element-1571" />
        <div className="mb-8 text-center" data-id="element-1572">
          <a href={CTA_URL} target="_blank" rel="noopener noreferrer" className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200" data-id="element-1573">
            Klik hier voor meer informatie over het webinar
          </a>
        </div>
        <Footer data-id="element-1574" />
      </div>
      <StickyMobileButton data-id="element-1575" />
    </div>;
}